import React, { useCallback, useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Suppress findDOMNode deprecation warning
if (typeof console !== 'undefined') {
  const originalConsoleError = console.error;
  console.error = (...args) => {
    if (typeof args[0] === 'string' && args[0].includes('findDOMNode is deprecated')) {
      return;
    }
    originalConsoleError.apply(console, args);
  };
}

if (typeof window !== 'undefined') {
  const originalAddEventListener = Element.prototype.addEventListener;
  Element.prototype.addEventListener = function(type, listener, options) {
    if (type === 'DOMNodeInserted' || type === 'DOMNodeRemoved') {
      return;
    }
    return originalAddEventListener.call(this, type, listener, options);
  };
}

const modules = {
  toolbar: [
    [{ size: ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline'],
    [{ color: [] }],
    [{ align: ['right', 'center'] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'link'],
    ['clean']
  ]
};

const formats = [
  'size',
  'bold',
  'italic',
  'underline',
  'color',
  'align',
  'list',
  'blockquote',
  'link'
];

const BasicInfo = ({ data, updateData }) => {
  const [editorContent, setEditorContent] = useState(data.description || '');
  const [learnContent, setLearnContent] = useState(data.whatYouWillLearn || '');

  const handleEditorChange = (content) => {
    setEditorContent(content);
    updateData({ description: content });
  };

  const handleLearnChange = (content) => {
    setLearnContent(content);
    updateData({ whatYouWillLearn: content });
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      updateData({ 
        coverImage: file,
        coverImagePreview: imageUrl
      });
    }
  }, [updateData]);

  const onPlanDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      updateData({ 
        planImage: file,
        planImagePreview: imageUrl
      });
    }
  }, [updateData]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    multiple: false
  });

  const { getRootProps: getPlanRootProps, getInputProps: getPlanInputProps, isDragActive: isDragPlanActive } = useDropzone({
    onDrop: onPlanDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    multiple: false
  });

  const handleRemoveCover = () => {
    URL.revokeObjectURL(data.coverImagePreview);
    updateData({
      coverImage: null,
      coverImagePreview: null
    });
  };

  const handleRemovePlan = () => {
    URL.revokeObjectURL(data.planImagePreview);
    updateData({
      planImage: null,
      planImagePreview: null
    });
  };

  // إضافة ref للـ ReactQuill
  const quillRef = useRef(null);

  return (
    <div className="space-y-6">
      {/* معلومات الدورة الأساسية */}
      <div className="relative my-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-blue-200 dark:border-blue-800"></div>
        </div>
        <div className="relative flex justify-center">
          <span className="bg-white dark:bg-gray-800 px-4 text-lg font-semibold text-blue-600 dark:text-blue-400">
            معلومات الدورة الأساسية
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* البطاقة اليمنى - معلومات الدورة */}
        <div className="p-6 bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
                اسم الدورة <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={data.name || ''}
                onChange={(e) => updateData({ name: e.target.value })}
                className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 focus:border-black dark:focus:border-white focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              />
            </div>

            <div>
              <label className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
                وصف الدورة <span className="text-red-500">*</span>
              </label>
              <div className="[&_.ql-toolbar.ql-snow]:rounded-t-md [&_.ql-container.ql-snow]:rounded-b-md dark:[&_.ql-toolbar.ql-snow]:rounded-t-md dark:[&_.ql-container.ql-snow]:rounded-b-md">
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={editorContent}
                  onChange={handleEditorChange}
                  modules={modules}
                  formats={formats}
                  className="bg-white dark:bg-gray-700 [&_.ql-container]:mb-0 [&_.ql-container]:h-auto [&_.ql-editor]:h-auto [&_.ql-editor]:min-h-[75px] [&_.ql-editor]:max-h-none [&_.ql-editor]:text-right [&_.ql-editor]:direction-rtl [&_.ql-toolbar]:direction-rtl [&_.ql-toolbar]:bg-white [&_.ql-toolbar]:dark:bg-gray-700 [&_.ql-toolbar]:dark:text-white [&_.ql-toolbar_.ql-stroke]:dark:stroke-white [&_.ql-toolbar_.ql-fill]:dark:fill-white [&_.ql-toolbar_.ql-picker-label]:dark:text-white [&_.ql-toolbar_.ql-picker-item]:dark:text-white [&_.ql-toolbar_.ql-picker-options]:dark:bg-gray-700 [&_.ql-toolbar.ql-snow]:border-gray-300 [&_.ql-container.ql-snow]:border-gray-300 [&_.ql-toolbar.ql-snow]:dark:border-gray-600 [&_.ql-container.ql-snow]:dark:border-gray-600 [&_.ql-editor]:text-gray-900 [&_.ql-editor]:dark:text-white"
                  dir="rtl"
                  placeholder="اكتب وصفاً للدورة..."
                />
              </div>
            </div>
             {/* ماذا ستتعلم ؟ */}
             <div>
               <label className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
                 ماذا ستتعلم ؟
               </label>
               <div className="[&_.ql-toolbar.ql-snow]:rounded-t-md [&_.ql-container.ql-snow]:rounded-b-md dark:[&_.ql-toolbar.ql-snow]:rounded-t-md dark:[&_.ql-container.ql-snow]:rounded-b-md">
                 <ReactQuill
                   ref={quillRef}
                   theme="snow"
                   value={learnContent}
                   onChange={handleLearnChange}
                   modules={modules}
                   formats={formats}
                   className="bg-white dark:bg-gray-700 [&_.ql-container]:mb-0 [&_.ql-container]:h-auto [&_.ql-editor]:h-auto [&_.ql-editor]:min-h-[75px] [&_.ql-editor]:max-h-none [&_.ql-editor]:text-right [&_.ql-editor]:direction-rtl [&_.ql-toolbar]:direction-rtl [&_.ql-toolbar]:bg-white [&_.ql-toolbar]:dark:bg-gray-700 [&_.ql-toolbar]:dark:text-white [&_.ql-toolbar_.ql-stroke]:dark:stroke-white [&_.ql-toolbar_.ql-fill]:dark:fill-white [&_.ql-toolbar_.ql-picker-label]:dark:text-white [&_.ql-toolbar_.ql-picker-item]:dark:text-white [&_.ql-toolbar_.ql-picker-options]:dark:bg-gray-700 [&_.ql-toolbar.ql-snow]:border-gray-300 [&_.ql-container.ql-snow]:border-gray-300 [&_.ql-toolbar.ql-snow]:dark:border-gray-600 [&_.ql-container.ql-snow]:dark:border-gray-600 [&_.ql-editor]:text-gray-900 [&_.ql-editor]:dark:text-white"
                   placeholder="اكتب ما سيتعلمه المتدرب..."
                 />
               </div>
             </div>
          </div>
        </div>

        {/* البطاقة اليسرى - الملفات */}
        <div className="p-6 bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="space-y-6">
            <div>
              <label className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
                غلاف الدورة <span className="text-red-500">*</span>
              </label>
              <div
                {...getRootProps()}
                className={`mt-1 flex flex-col justify-center items-center h-48 w-full border-2 border-dashed rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 relative
                  ${isDragActive
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900'
                    : 'border-gray-300 dark:border-gray-600'
                  }`}
              >
                <div className="space-y-2 text-center h-full w-full flex flex-col justify-center items-center">
                  <input {...getInputProps()} />
                  {data.coverImagePreview ? (
                    <div className="relative w-full h-full group">
                      <img
                        src={data.coverImagePreview}
                        alt="معاينة غلاف الدورة"
                        className="w-full h-full object-cover rounded-md"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveCover();
                        }}
                        className="absolute top-2 left-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <>
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-300"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="flex text-sm text-gray-600 dark:text-gray-300 justify-center">
                        <p className="relative cursor-pointer rounded-md font-medium text-black dark:text-white hover:text-blue-500 dark:hover:text-blue-300 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                          {isDragActive ? 'أفلت الملف هنا' : 'اضغط لاختيار صورة أو اسحب وأفلت'}
                        </p>
                      </div>
                      <p className="text-xs text-black dark:text-white">PNG, JPG حتى 10MB</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
                خطة الدورة <span className="text-red-500">*</span>
              </label>
              <div
                {...getPlanRootProps()}
                className={`mt-1 flex flex-col justify-center items-center h-48 w-full border-2 border-dashed rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 relative
                  ${isDragPlanActive
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900'
                    : 'border-gray-300 dark:border-gray-600'
                  }`}
              >
                <div className="space-y-2 text-center h-full w-full flex flex-col justify-center items-center">
                  <input {...getPlanInputProps()} />
                  {data.planImagePreview ? (
                    <div className="relative w-full h-full group">
                      <img
                        src={data.planImagePreview}
                        alt="معاينة خطة الدورة"
                        className="w-full h-full object-cover rounded-md"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemovePlan();
                        }}
                        className="absolute top-2 left-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <>
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-300"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="flex text-sm text-gray-600 dark:text-gray-300 justify-center">
                        <p className="relative cursor-pointer rounded-md font-medium text-black dark:text-white hover:text-blue-500 dark:hover:text-blue-300 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                          {isDragPlanActive ? 'أفلت الملف هنا' : 'اضغط لاختيار صورة أو اسحب وأفلت'}
                        </p>
                      </div>
                      <p className="text-xs text-black dark:text-white">PNG, JPG حتى 10MB</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInfo;
